import React, { useState, useEffect } from 'react';
import { SquaresPlusIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { executeAgent } from '../../services/agentService';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { Step } from '../../types';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const ThemeExplorer: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  // Timer para atualizar o tempo decorrido
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      setError('Por favor, digite um tema.');
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference)) {
      setShowSecurityModal(true);
      return;
    }

    setIsLoading(true);
    setError('');
    setResult('');
    setHasStarted(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSteps([{ id: '1', title: 'Explorando tema...', status: 'running' }]);

    try {
      const parameters = {
        prompt: `Explore temas contemporâneos relevantes conectando-os à mensagem bíblica integral sobre: ${biblicalReference}`,
        tema: biblicalReference
      };

      const data = await executeAgent('explorar-temas', parameters);
      
      // Extrair texto da resposta (pode vir como JSON ou texto direto)
      let resultText = data.text || '';
      
      // Se a resposta vier como JSON string, tentar parsear e converter para markdown
      if (resultText.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(resultText);
          
          // Tentar extrair campos comuns primeiro
          if (parsed.report) {
            resultText = parsed.report;
          } else if (parsed.text) {
            resultText = parsed.text;
          } else if (parsed.content) {
            resultText = parsed.content;
          } else if (parsed.resposta) {
            resultText = parsed.resposta;
          } else if (parsed.output) {
            resultText = parsed.output;
          } else if (parsed.dados) {
            // Se o campo for "dados", converter para markdown
            resultText = typeof parsed.dados === 'string' 
              ? parsed.dados 
              : JSON.stringify(parsed.dados, null, 2);
          } else {
            // Converter JSON para markdown formatado
            let markdownParts: string[] = [];
            
            // Se tiver campo 'theme', usar como título
            if (parsed.theme) {
              markdownParts.push(`# ${parsed.theme}\n\n`);
            }
            
            // Se tiver campo 'passages', formatar como lista
            if (parsed.passages && Array.isArray(parsed.passages)) {
              markdownParts.push('## Passagens Bíblicas Relevantes\n\n');
              parsed.passages.forEach((passage: any, index: number) => {
                if (passage.reference) {
                  markdownParts.push(`### ${index + 1}. ${passage.reference}\n\n`);
                }
                if (passage.justification) {
                  markdownParts.push(`${passage.justification}\n\n`);
                }
              });
            }
            
            // Se tiver outros campos, adicionar como seções
            Object.keys(parsed).forEach(key => {
              if (!['theme', 'passages', 'report', 'text', 'content', 'resposta', 'output', 'dados'].includes(key)) {
                const value = parsed[key];
                if (typeof value === 'string') {
                  markdownParts.push(`## ${key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}\n\n${value}\n\n`);
                } else if (typeof value === 'object' && value !== null) {
                  markdownParts.push(`## ${key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}\n\n${JSON.stringify(value, null, 2)}\n\n`);
                }
              }
            });
            
            if (markdownParts.length > 0) {
              resultText = markdownParts.join('');
            } else {
              // Se não conseguiu converter, usar o JSON formatado como fallback
              resultText = JSON.stringify(parsed, null, 2);
            }
          }
        } catch {
          // Se não for JSON válido, usar o texto original
        }
      }
      
      // Fallback: se resultText estiver vazio após processamento, usar data.text diretamente
      if (!resultText || resultText.trim().length === 0) {
        if (data.text && data.text.trim().length > 0) {
          resultText = data.text;
        } else {
          resultText = 'Nenhum resultado foi retornado pelo agente.';
        }
      }
      
      setResult(resultText);
      setSteps([{ id: '1', title: 'Tema explorado', status: 'success' }]);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao executar o agente';
      setError(`Erro: ${errorMessage}`);
      setSteps([{ id: '1', title: 'Erro ao explorar tema', status: 'error', error: errorMessage }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setBiblicalReference('');
    setResult('');
    setError('');
    setSteps([]);
    setHasStarted(false);
    setStartTime(null);
    setElapsedTime(0);
  };

  return (
    <AgentPageLayout
      icon={<SquaresPlusIcon className="w-6 h-6 text-secondary" />}
      title="Explorar Temas"
      subtitle="Explore temas contemporâneos relevantes conectando-os à mensagem bíblica integral."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AgentFormGroup>
            <AgentLabel htmlFor="biblicalReference">Digite um Tema</AgentLabel>
            <AgentInput
              id="biblicalReference"
              type="text"
              value={biblicalReference}
              onChange={(e) => setBiblicalReference(e.target.value)}
              placeholder="Ex. Amor, Vergonha, Medo"
              disabled={isLoading}
            />
          </AgentFormGroup>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Explorando Tema...</span>
                </>
              ) : (
                <>
                  <SquaresPlusIcon className="w-5 h-5" />
                  <span>Explorar Tema</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} />}

        {isLoading && (
          <section className="mt-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😇</span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                  </h3>
                </div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                {steps.length > 0 && steps[0]?.title && (
                  <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                )}
                {elapsedTime > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Tempo de execução: {formatElapsedTime(elapsedTime)}
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {result && (
          <AgentResult title="Temas Explorados">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {result}
            </ReactMarkdown>
            <AgentDisclaimer />
            <div className="mt-6 flex items-center justify-end gap-3">
              <AgentOutputActions
                content={result}
                agentName="Explorar Temas"
                agentCode="explorar-temas"
                reference={biblicalReference}
              />
              <AgentButton
                type="button"
                onClick={handleReset}
                variant="secondary"
                aria-label="Reiniciar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span>Reiniciar</span>
              </AgentButton>
            </div>
          </AgentResult>
        )}
      </AgentFormSection>

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
    </AgentPageLayout>
  );
};

export default ThemeExplorer;
